import React, { FC, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Avatar } from '@/components/avatar';
import {
  AccountContext,
  AccountContextProps,
} from '@/features/account-context';
import { HeaderPopover } from '@/components/headerPopover';
import { BackToLive } from '@/components/BackToLive';
import { editorRoute, Pages } from '@/webapi/pages';
import { nav } from '@/utils/browser';
import { NewExperienceButton } from '@/features/dashboard/experiences/new-experience-button';
import { LogoSection } from '@/components/logo-section';

export const Header: FC<{
  headerControls?: any;
  disabledAccount?: boolean;
  hideBackToLive?: boolean;
  className?: string;
}> = ({ headerControls, disabledAccount, hideBackToLive, className }) => {
  const ctx = useContext(AccountContext);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);

  const onNewExperience = async () => {
    await nav(editorRoute());
  };

  const trialInfo = useMemo(() => {
    const trialEnds = ctx?.subscription?.trialEnds;
    if (!trialEnds) return null;

    const trialEndsDate = new Date(trialEnds);
    const currentDate = new Date();

    if (trialEndsDate <= currentDate) return null;

    const timeDiff = trialEndsDate.getTime() - currentDate.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return { daysRemaining };
  }, [ctx?.subscription?.trialEnds]);

  return (
    <Wrapper className={className}>
      <section>
        <LogoSection store={ctx?.account?.store} />
      </section>
      <section>
        <ControlsSection>{headerControls || <div />}</ControlsSection>
      </section>
      <section>
        <NewExperienceButton onClick={onNewExperience}>
          Create New Experience
        </NewExperienceButton>
        <AccountSection>
          <AvatarContainer>
            <HeaderPopover
              disabledAccount={disabledAccount}
              onLogout={ctx.logout}
              onPopoverOpenChange={setIsAvatarMenuOpen}
            >
              <Avatar
                size="3.9rem"
                profilePic={ctx?.account?.store?.profilePic}
                wrapperStyles={{
                  background: `#ffffff`,
                  boxShadow: `0 4px 6px 0 rgba(215,215,215,0.45)`,
                  color: `#91A0AF`,
                  fontWeight: `500`,
                  fontSize: `1.3rem`,
                  fontFamily: `Inter`,
                }}
              >
                {firstLetters(ctx)}
              </Avatar>
            </HeaderPopover>
            {trialInfo && !isAvatarMenuOpen && (
              <TrialBadge>{`${trialInfo.daysRemaining} trial days left`}</TrialBadge>
            )}
          </AvatarContainer>
        </AccountSection>
        {!hideBackToLive && (
          <BackToLive id="back_btn" onClick={() => nav(Pages.DASHBOARD)} />
        )}
      </section>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  background: rgb(248, 249, 251);
  height: 70px;
  width: 100%;
  padding: 2rem 3rem;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: 1.5rem;

  && > section {
    display: flex;

    :nth-child(1) {
      justify-content: flex-start;
      align-items: center;
    }

    :nth-child(2) {
      justify-content: center;
      align-items: center;
    }

    :nth-child(3) {
      display: grid;
      grid-template-columns: max-content max-content;
      justify-content: flex-end;
      align-items: center;
      grid-column-gap: 1.5rem;
    }
  }
`;

const ControlsSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  * {
    font-family: 'Eesti', serif !important;
    font-size: 1.2rem !important;
    font-weight: 800 !important;
  }
`;

const AccountSection = styled.div`
  height: 100%;
  width: 100%;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;

  button {
    border: none;
    background: none;
  }
`;

const AvatarContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TrialBadge = styled.div`
  margin-top: 0.7rem;
  background: #e8ecf0;
  border-radius: 1rem;
  padding: 1rem 0.8rem;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  color: #000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  position: absolute;
  top: calc(100% + 0.3rem);
  right: 0;
  text-align: center;
  white-space: nowrap;

  &::before {
    content: '';
    position: absolute;
    top: -0.6rem;
    left: 70%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 0.6rem solid transparent;
    border-right: 0.6rem solid transparent;
    border-bottom: 0.6rem solid #e8ecf0;
  }
`;

export function firstLetters(ctx: AccountContextProps) {
  const store = ctx?.account?.store;
  if (!store?.firstName) {
    return ``;
  }
  return `${store?.firstName?.[0]?.toUpperCase() || ``}${
    store?.lastName?.[0]?.toUpperCase() || ``
  }`;
}
